import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  serializeNumber,
  validation,
  validationError,
} from './shared.ts';

interface BatteryManagerLike {
  readonly charging: boolean;
  readonly chargingTime: number;
  readonly dischargingTime: number;
  readonly level: number;
}

declare global {
  interface Navigator {
    getBattery?: () => Promise<BatteryManagerLike>;
  }
}

async function collect(): Promise<RawData> {
  if (!navigator.getBattery) {
    return { available: false };
  }
  const battery = await navigator.getBattery();

  return {
    charging: battery.charging,
    chargingTime: serializeNumber(battery.chargingTime),
    dischargingTime: serializeNumber(battery.dischargingTime),
    level: battery.level,
  };
}

function isValidTime(value: unknown): boolean {
  if (value == 'Infinity') {
    return true;
  }
  return typeof value == 'number' && !Number.isNaN(value) && value >= 0;
}

function validateBattery(rawData: RawData): ValidationResult {
  if (rawData.available == false) {
    return validation([]);
  }
  const reasons: string[] = [];
  const level = rawData.level;
  if (typeof level != 'number' || level < 0 || level > 1) {
    reasons.push('Battery level must be between 0 and 1');
  }
  if (typeof rawData.charging != 'boolean') {
    reasons.push('Battery charging must be boolean');
  }
  if (!isValidTime(rawData.chargingTime)) {
    reasons.push('Battery charging time must be a nonnegative number');
  }
  if (!isValidTime(rawData.dischargingTime)) {
    reasons.push('Battery discharging time must be a nonnegative number');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  if (rawData.available == false) {
    return 'Unavailable';
  }
  return `${Math.round(Number(rawData.level) * 100)}% · ${rawData.charging ? 'charging' : 'battery'}`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateBattery(rawData);
}

export const batteryTest: TestDefinition = {
  id: 'battery',
  title: 'Battery',
  category: 'variableProfile',
  format,
  run,
  validate,
};
